import React from 'react';
import { Link } from 'react-router-dom';
import {
    AlarmClock,
    Bell,
    CheckCircle2,
    MessageSquare,
    PencilLine,
    UserPlus,
} from 'lucide-react';
import {
    useNotifications,
    useMarkNotificationAsRead,
    useMarkAllNotificationsAsRead,
} from '../hooks/useApi';
import Dropdown from './ui/Dropdown';
import { EmptyState } from './ui/States';
import { ListSkeleton } from './ui/Skeleton';

const formatRelativeTime = (date) => {
    const diffInSeconds = Math.floor((Date.now() - date) / 1000);
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    if (diffInSeconds < 604800) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    return date.toLocaleDateString();
};

const TYPE_META = {
    assignment: { icon: UserPlus, tone: 'bg-info-subtle text-info-strong' },
    deadline: { icon: AlarmClock, tone: 'bg-warning-subtle text-warning-strong' },
    update: { icon: PencilLine, tone: 'bg-ink-100 text-ink-600' },
    comment: { icon: MessageSquare, tone: 'bg-primary-50 text-primary-600' },
    approval: { icon: CheckCircle2, tone: 'bg-success-subtle text-success-strong' },
};

const NotificationDropdown = () => {
    const { data: notificationsData, isLoading } = useNotifications();
    const markAsRead = useMarkNotificationAsRead();
    const markAllAsRead = useMarkAllNotificationsAsRead();

    const notifications = notificationsData?.data || [];
    const unreadCount = notificationsData?.unread_count || 0;

    const getNotificationLink = (n) => {
        switch (n.entity_type) {
            case 'task':
                return `/tasks/${n.entity_id}`;
            case 'project':
                return `/projects/${n.entity_id}`;
            case 'meeting':
                return `/meetings/${n.entity_id}`;
            case 'ticket':
                return `/tickets/${n.entity_id}`;
            default:
                return '/notifications';
        }
    };

    return (
        <Dropdown
            align="right"
            panelClassName="w-[340px] max-w-[calc(100vw-2rem)] p-0 overflow-hidden"
            trigger={
                <button
                    type="button"
                    className="relative p-2 rounded-md text-ink-500 hover:bg-ink-100 hover:text-ink-800 transition-colors duration-150"
                    aria-label={
                        unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
                    }
                >
                    <Bell size={18} aria-hidden="true" />
                    {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 min-w-[15px] h-[15px] px-1 rounded-full bg-danger text-white text-[9px] font-semibold leading-[15px] text-center ring-2 ring-white">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </button>
            }
        >
            <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-[var(--divider-color)]">
                <h3 className="text-md font-semibold text-ink-900">
                    Notifications
                    {unreadCount > 0 && (
                        <span className="ml-1.5 text-label font-normal text-ink-500">
                            {unreadCount} unread
                        </span>
                    )}
                </h3>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            markAllAsRead.mutate();
                        }}
                        className="text-label font-medium text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                    >
                        Mark all read
                    </button>
                )}
            </div>

            <div className="max-h-[380px] overflow-y-auto">
                {isLoading ? (
                    <div className="p-4">
                        <ListSkeleton rows={3} />
                    </div>
                ) : notifications.length === 0 ? (
                    <EmptyState
                        compact
                        icon={Bell}
                        title="You're all caught up"
                        description="New assignments and deadline reminders will appear here."
                    />
                ) : (
                    <ul className="divide-y divide-[var(--divider-color)]">
                        {notifications.map((n) => {
                            const meta = TYPE_META[n.type] || { icon: Bell, tone: 'bg-ink-100 text-ink-600' };
                            const Icon = meta.icon;
                            return (
                                <li key={n.id}>
                                    <Link
                                        to={getNotificationLink(n)}
                                        onClick={() => !n.is_read && markAsRead.mutate(n.id)}
                                        className={`flex gap-2.5 px-3.5 py-3 hover:bg-[var(--bg-subtle)] transition-colors duration-150 ${!n.is_read ? 'bg-primary-50/40' : ''
                                            }`}
                                    >
                                        <span
                                            className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${meta.tone}`}
                                            aria-hidden="true"
                                        >
                                            <Icon size={14} />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="flex items-start gap-2">
                                                <span
                                                    className={`block text-base truncate ${!n.is_read ? 'font-semibold text-ink-900' : 'text-ink-700'
                                                        }`}
                                                >
                                                    {n.title}
                                                </span>
                                                {!n.is_read && (
                                                    <span
                                                        className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary-500 shrink-0"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                            </span>
                                            <span className="block text-label text-ink-500 line-clamp-2 mt-0.5">
                                                {n.message}
                                            </span>
                                            <span className="block text-micro text-ink-400 mt-1">
                                                {formatRelativeTime(new Date(n.created_at))}
                                            </span>
                                        </span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="border-t border-[var(--divider-color)] px-3.5 py-2 text-center">
                <Link
                    to="/notifications"
                    className="text-label font-medium text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                >
                    View all notifications
                </Link>
            </div>
        </Dropdown>
    );
};

export default NotificationDropdown;
